'use client';
import { MutableRefObject } from 'react';
import Icon from '../Icon';
import { KanbanColumn } from '@/lib/vster/types';

interface Props {
  navigate: (s: string) => void;
  board: KanbanColumn[];
  dragOverCol: number;
  menuCol: number;
  addingCol: number;
  addingText: string;
  editingCol: number;
  editingText: string;
  dragSrc: MutableRefObject<{ ci: number; di: number } | null>;
  setDragOverCol: (n: number) => void;
  moveCard: (dstCol: number, dstIndex: number) => void;
  toggleMenu: (ci: number) => void;
  clearColumn: (ci: number) => void;
  deleteColumn: (ci: number) => void;
  startEdit: (ci: number) => void;
  commitEdit: () => void;
  startAdd: (ci: number) => void;
  commitAdd: () => void;
  setAddingText: (t: string) => void;
  setEditingText: (t: string) => void;
  cancelAdd: () => void;
  closeMenu: () => void;
  addColumn: () => void;
}

export default function KanbanScreen({ navigate, board, dragOverCol, menuCol, addingCol, addingText, editingCol, editingText, dragSrc, setDragOverCol, moveCard, toggleMenu, clearColumn, deleteColumn, startEdit, commitEdit, startAdd, commitAdd, setAddingText, setEditingText, cancelAdd, closeMenu, addColumn }: Props) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
        <div>
          <h1 className="vh1" style={{ fontSize: 32, fontWeight: 700, letterSpacing: '-.015em', margin: '0 0 4px' }}>칸반 트래킹 보드</h1>
          <p style={{ fontSize: 16, color: '#434654', margin: 0 }}>이번 달 <strong style={{ color: '#003d9b' }}>6건</strong> 진행 중 · <strong style={{ color: '#ba1a1a' }}>2건</strong> 마감 임박</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4, background: '#e9edff', padding: 4, borderRadius: 10 }}>
            {['전체', '공고만', '행사만'].map((label, i) => (
              <span key={label} style={{ padding: '7px 13px', borderRadius: 7, background: i === 0 ? '#fff' : 'transparent', color: i === 0 ? '#003d9b' : '#434654', fontSize: 13, fontWeight: 600, boxShadow: i === 0 ? '0 1px 4px rgba(5,26,62,.08)' : 'none', cursor: 'pointer' }}>{label}</span>
            ))}
          </div>
          <button style={{ background: '#003d9b', color: '#fff', border: 'none', cursor: 'pointer', padding: '9px 16px', borderRadius: 10, fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Icon name="add" size={18} color="#fff" />카드 추가
          </button>
        </div>
      </div>

      {menuCol !== -1 && (
        <div onClick={closeMenu} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
      )}

      <div className="vrow x-scroll" style={{ display: 'flex', gap: 16, overflowX: 'auto', padding: '18px 0 10px', alignItems: 'flex-start' }}>
        {board.map((col, ci) => (
          <div
            key={ci}
            onDragOver={e => { e.preventDefault(); if (dragOverCol !== ci) setDragOverCol(ci); }}
            onDrop={e => { e.preventDefault(); moveCard(ci, col.cards.length); }}
            style={{ flexShrink: 0, width: 280, background: dragOverCol === ci ? '#dfe7ff' : '#f1f3ff', border: dragOverCol === ci ? '1.5px dashed #003d9b' : '1px solid #e4e8f6', borderRadius: 14, padding: 12, display: 'flex', flexDirection: 'column', gap: 10, transition: 'background .15s ease', position: 'relative', zIndex: menuCol === ci ? 25 : 'auto' }}
          >
            {/* 컬럼 헤더 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px', position: 'relative' }}>
              {editingCol === ci ? (
                <input
                  value={editingText}
                  onChange={e => setEditingText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') commitEdit(); }}
                  onBlur={commitEdit}
                  autoFocus
                  style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, padding: '5px 9px', border: '1.5px solid #003d9b', borderRadius: 8, outline: 'none', fontFamily: 'inherit', background: '#fff', color: '#051a3e' }}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: col.accent, flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{col.name}</span>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: '#737685', background: '#fff', padding: '1px 8px', borderRadius: 999, flexShrink: 0 }}>{col.cards.length}</span>
                </div>
              )}
              <button onClick={() => toggleMenu(ci)} style={{ background: 'none', border: 'none', cursor: 'pointer', width: 26, height: 26, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="more_horiz" size={18} color="#737685" />
              </button>

              {/* 드롭다운 메뉴 */}
              {menuCol === ci && (
                <div style={{ position: 'absolute', top: 32, right: 4, zIndex: 30, background: '#fff', border: '1px solid #e4e8f6', borderRadius: 11, boxShadow: '0 10px 28px rgba(5,26,62,.16)', padding: 6, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 150 }}>
                  {[
                    { icon: 'edit', label: '이름 변경', onClick: () => startEdit(ci) },
                    { icon: 'add', label: '카드 추가', onClick: () => startAdd(ci) },
                    { icon: 'backspace', label: '모두 비우기', onClick: () => clearColumn(ci) },
                  ].map(item => (
                    <button key={item.label} onClick={item.onClick} style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'none', border: 'none', cursor: 'pointer', padding: '9px 11px', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#434654', textAlign: 'left', width: '100%' }}>
                      <Icon name={item.icon} size={17} color="#737685" />{item.label}
                    </button>
                  ))}
                  <div style={{ height: 1, background: '#eef1fb', margin: '3px 6px' }} />
                  <button onClick={() => deleteColumn(ci)} style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'none', border: 'none', cursor: 'pointer', padding: '9px 11px', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#ba1a1a', textAlign: 'left', width: '100%' }}>
                    <Icon name="delete" size={17} color="#ba1a1a" />컬럼 삭제
                  </button>
                </div>
              )}
            </div>

            {/* 카드 목록 */}
            {col.cards.map((cd, di) => (
              <div
                key={cd.id}
                draggable
                onDragStart={e => { dragSrc.current = { ci, di }; try { e.dataTransfer.effectAllowed = 'move'; } catch (_) {} setDragOverCol(ci); }}
                onDragEnd={() => { dragSrc.current = null; setDragOverCol(-1); }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); e.stopPropagation(); moveCard(ci, di); }}
                style={{ background: '#fff', border: cd.urgent ? '1.5px solid #ba1a1a' : '1px solid #e4e8f6', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 9, boxShadow: '0 2px 8px rgba(5,26,62,.04)', cursor: 'grab' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <Icon name="drag_indicator" size={15} color="#c3c6d6" />
                    <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, padding: '3px 8px', borderRadius: 6, background: '#f1f3ff', color: '#434654' }}>{cd.tag}</span>
                  </div>
                  {cd.result
                    ? <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: cd.resBg, color: cd.resColor }}>{cd.result}</span>
                    : <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: cd.ddBg, color: cd.ddColor }}>{cd.dday}</span>
                  }
                </div>
                <h4 style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3, margin: 0 }}>{cd.title}</h4>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#003d9b' }}>{cd.amount}</span>
                {cd.memo && (
                  <div style={{ background: '#f1f3ff', borderRadius: 8, padding: '8px 10px', fontSize: 12, color: '#434654', lineHeight: 1.45, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <Icon name="sticky_note_2" size={14} color="#737685" style={{ marginTop: 1 }} />{cd.memo}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6, borderTop: '1px solid #eef1fb', paddingTop: 9 }}>
                  <button onClick={() => navigate('planner')} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', color: '#003d9b', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: 4 }}>
                    <Icon name="calendar_month" size={15} />일정
                  </button>
                  <button onClick={() => navigate('detail')} style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', color: '#434654', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: 4 }}>
                    <Icon name="open_in_new" size={15} />상세
                  </button>
                </div>
              </div>
            ))}

            {/* 카드 추가 인풋 */}
            {addingCol === ci ? (
              <div style={{ background: '#fff', border: '1.5px solid #003d9b', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  value={addingText}
                  onChange={e => setAddingText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') commitAdd(); if (e.key === 'Escape') cancelAdd(); }}
                  placeholder="공고명을 입력하고 Enter"
                  autoFocus
                  style={{ width: '100%', fontSize: 14, padding: '7px 9px', border: '1px solid #c3c6d6', borderRadius: 8, outline: 'none', fontFamily: 'inherit', color: '#051a3e' }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={commitAdd} style={{ flex: 1, background: '#003d9b', color: '#fff', border: 'none', cursor: 'pointer', padding: 7, borderRadius: 8, fontSize: 13, fontWeight: 600 }}>추가</button>
                  <button onClick={cancelAdd} style={{ background: '#e9edff', color: '#434654', border: 'none', cursor: 'pointer', padding: '7px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600 }}>취소</button>
                </div>
              </div>
            ) : (
              <button onClick={() => startAdd(ci)} style={{ background: 'none', border: '1.5px dashed #c3c6d6', borderRadius: 10, padding: 10, cursor: 'pointer', color: '#737685', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                <Icon name="add" size={17} />추가
              </button>
            )}
          </div>
        ))}

        {/* 단계 추가 버튼 */}
        <button onClick={addColumn} style={{ flexShrink: 0, width: 200, alignSelf: 'flex-start', background: 'none', border: '1.5px dashed #c3c6d6', borderRadius: 14, padding: 14, cursor: 'pointer', color: '#737685', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <Icon name="add" size={19} />단계 추가
        </button>
      </div>
    </div>
  );
}
